/* eslint-disable */
/**
 * Perfiles, bandas y visualizaciones de profundidad.
 *
 * Estos métodos operan sobre la instancia de PanelLogic y conservan el
 * contrato del prototipo importado. No mantienen estado global propio.
 */
const logic = {
  depthBands(det, bm, a0, a1) {
    const P = det.pts,
      B = [];
    for (let a = a0; a < a1; a += bm) {
      const b = a + bm,
        inB = p => p.dep >= a && p.dep < b,
        ps = P.filter(inB),
        feed = ps.filter(p => p.tasa > 0).length * 5;
      B.push({
        a,
        b,
        feed,
        idle: ps.length * 5 - feed,
        ppa: ps.reduce((s, p) => s + p.ppa, 0),
        kg: ps.reduce((s, p) => s + p.tasa * 5, 0),
        al: det.alarms.filter(x => inB(P[x.i])).length
      });
    }
    return B;
  },
  depthProfile(det, H) {
    const st = this.state,
      P = det.pts,
      deps = P.map(p => p.dep),
      dL = Math.max(...deps) + 0.5,
      met = st.dMet || 'ppa';
    const Yv = v => 5 + v / dL * 90,
      bm = H * 0.9 / dL < 11 ? 2 : 1;
    const B = this.depthBands(det, bm, Math.floor(Math.min(...deps) / bm) * bm, Math.ceil((dL - 0.5) / bm) * bm);
    const tF = B.reduce((s, x) => s + x.feed, 0),
      tP = B.reduce((s, x) => s + x.ppa, 0);
    const mv = x => met === 'al' ? x.al : x.ppa,
      mM = Math.max(1e-9, ...B.map(mv));
    const dHeat = B.filter(x => x.feed + x.idle).map(x => {
      const v = mv(x),
        k = v / mM,
        y0 = Yv(x.a),
        y1 = Yv(Math.min(x.b, dL));
      return {
        top: y0.toFixed(2) + '%',
        h: (y1 - y0).toFixed(2) + '%',
        bg: v > (met === 'al' ? 0 : 0.5) ? met === 'al' ? 'oklch(0.6 0.19 25 / ' + (0.22 + 0.5 * k).toFixed(2) + ')' : 'rgba(184,193,206,' + (0.07 + 0.38 * k).toFixed(2) + ')' : 'rgba(46,179,238,0.04)'
      };
    });
    const cand = B.filter(x => x.feed >= 10),
      avg = tF ? tP / tF : 0,
      crit = cand.length && avg ? cand.reduce((m, x) => x.ppa / x.feed > m.ppa / m.feed ? x : m) : null;
    const dIns = !tF ? {
      lab: 'Ayuno',
      c: '#8793a6',
      txt: '· sin alimentación'
    } : crit && crit.ppa > 0.5 ? {
      lab: 'Crítico ' + crit.a + '-' + crit.b + ' m',
      c: 'oklch(0.72 0.17 25)',
      txt: '· ' + (crit.ppa / crit.feed / avg).toFixed(1) + '× PPA/min'
    } : {
      lab: 'Sin pérdida',
      c: 'oklch(0.78 0.14 160)',
      txt: 'por estrato'
    };
    const dMetOpts = [['ppa', 'PPA', 'Fondo por estrato = pellets perdidos'], ['al', 'Alarmas', 'Fondo por estrato = alarmas']].map(([k, label, title]) => ({
      label,
      title,
      bg: met === k ? 'rgba(46,179,238,0.16)' : 'transparent',
      color: met === k ? '#e4e9f0' : '#8793a6',
      onClick: () => this.setState({
        dMet: k
      })
    }));
    return {
      dHeat,
      dIns,
      dMetOpts
    };
  },
  depVals(st) {
    const D = this.data,
      N = D.days.length,
      scope = st.dpScope || 'all',
      per = st.dpPer || 'all',
      met = st.dpMet || 'ppa';
    const end = per === 'all' ? N - 1 : st.day,
      start = per === 'all' ? 0 : Math.max(0, end - per + 1),
      days = [];
    for (let d = start; d <= end; d++) days.push(d);
    const cols = scope === 'all' ? D.cages.map((name, ci) => ({
      name,
      ci,
      ds: days
    })) : days.map(d => ({
      name: D.days[d],
      ci: st.sel,
      ds: [d],
      d
    }));
    const M = cols.map(c => {
      const acc = [...Array(24)].map((_, a) => ({
        a,
        feed: 0,
        idle: 0,
        ppa: 0,
        kg: 0,
        al: 0
      }));
      let fast = true;
      c.ds.forEach(d => {
        const g = this.depAgg(c.ci, d);
        if (!g.fast) fast = false;
        g.B.forEach((x, j) => {
          const y = acc[j];
          y.feed += x.feed;
          y.idle += x.idle;
          y.ppa += x.ppa;
          y.kg += x.kg;
          y.al += x.al;
        });
      });
      return {
        acc,
        fast
      };
    });
    const used = [...Array(24).keys()].filter(j => M.some(m => m.acc[j].feed + m.acc[j].idle > 0)),
      j0 = Math.min(...used),
      j1 = Math.max(...used),
      J = [];
    for (let j = j0; j <= j1; j++) J.push(j);
    const W = {
      rep: 0,
      op: 0,
      tr: 0,
      nd: 0,
      alN: 0,
      alLow: 0,
      dMin: 99,
      dMax: 0,
      eq: new Set()
    };
    cols.forEach(c => c.ds.forEach(d => {
      const g = this.depAgg(c.ci, d);
      W.rep += g.rep;
      W.op += g.op;
      W.tr += g.tr;
      W.nd += g.nd;
      W.alN += g.alN;
      W.alLow += g.alLow;
      W.dMin = Math.min(W.dMin, g.dMin);
      W.dMax = Math.max(W.dMax, g.dMax);
      W.eq.add(c.ci);
    }));
    const val = x => met === 'ppa' ? x.ppa : met === 'al' ? x.al : met === 'feed' ? x.feed : x.feed >= 10 ? x.ppa / x.feed : null;
    const fmtV = v => v == null ? '–' : met === 'feed' ? v >= 60 ? (v / 60).toFixed(1) + ' h' : Math.round(v) + ' min' : met === 'ppm' ? v.toFixed(2) : String(Math.round(v));
    const allV = [];
    M.forEach(m => J.forEach(j => {
      const v = val(m.acc[j]);
      if (v != null && v > 0) allV.push(v);
    }));
    allV.sort((a, b) => a - b);
    const q = p => allV.length ? allV[Math.min(allV.length - 1, Math.floor(p * (allV.length - 1)))] : 0,
      lo = q(0.1),
      mx = Math.max(lo + 1e-9, q(0.95));
    const hue = met === 'feed' ? 55 : 25;
    const color = (x, fast) => {
      if (fast && x.feed + x.idle === 0) return '#0a1220';
      if (x.feed + x.idle === 0) return 'transparent';
      const v = val(x);
      if (v == null || v <= 0) return 'rgba(46,179,238,0.07)';
      const k = Math.max(0, Math.min(1, (v - lo) / (mx - lo)));
      return 'oklch(' + (0.34 + 0.42 * k).toFixed(3) + ' ' + (0.05 + 0.15 * k).toFixed(3) + ' ' + hue + ')';
    };
    const hov = st.dpHov,
      nC = cols.length;
    const tot = J.map(j => {
      const s = {
        feed: 0,
        idle: 0,
        ppa: 0,
        kg: 0,
        al: 0
      };
      M.forEach(m => {
        const x = m.acc[j];
        for (const k in s) s[k] += x[k];
      });
      return s;
    });
    const tv = tot.map(val),
      tMx = Math.max(1e-9, ...tv.filter(v => v != null));
    const go = c => () => this.setState(c.d != null ? {
      view: 'panel',
      day: c.d
    } : {
      view: 'panel',
      sel: c.ci
    });
    const depRows = J.map((j, r) => ({
      lab: j + '-' + (j + 1),
      labC: hov && hov.r === r ? '#e4e9f0' : '#8793a6',
      cells: cols.map((c, ci) => {
        const x = M[ci].acc[j],
          on = hov && hov.r === r && hov.c === ci;
        return {
          bg: color(x, M[ci].fast),
          ol: on ? "0.092593vh solid #e4e9f0" : 'none',
          enter: () => this.setState({
            dpHov: {
              r,
              c: ci
            }
          }),
          click: go(c)
        };
      }),
      totW: tv[r] == null ? '0' : (tv[r] / tMx * 100).toFixed(1) + '%',
      totV: fmtV(tv[r])
    }));
    const lbStep = nC > 40 ? 3 : nC > 20 ? 2 : 1;
    const depHead = cols.map((c, ci) => ({
      lab: ci % lbStep === 0 ? c.name : '',
      c: hov && hov.c === ci ? '#e4e9f0' : c.ci === st.sel && scope === 'all' || c.d === st.day ? 'oklch(0.78 0.12 250)' : '#8793a6'
    }));
    const fm = m => m < 60 ? Math.round(m) + ' min' : Math.floor(m / 60) + ' h ' + String(Math.round(m % 60)).padStart(2, '0') + ' min';
    let depTip = {
      on: false,
      rows: []
    };
    if (hov && hov.r < J.length && hov.c < nC) {
      const x = M[hov.c].acc[J[hov.r]],
        c = cols[hov.c];
      depTip = {
        on: true,
        title: (scope === 'all' ? c.name : D.cages[st.sel] + ' · ' + c.name) + ' · ' + J[hov.r] + '-' + (J[hov.r] + 1) + ' m',
        left: "calc(2.916667vw + (100% - 9.6875vw) * " + ((hov.c + 0.5) / nC).toFixed(4) + ')',
        top: ((hov.r + 0.5) / J.length * 100).toFixed(2) + '%',
        tx: (hov.c + 0.5) / nC > 0.6 ? "translate(calc(-100% - 0.729167vw),-50%)" : "translate(0.729167vw,-50%)",
        rows: [{
          l: '▸ Alimentando',
          c: 'oklch(0.72 0.14 55)',
          v: fm(x.feed)
        }, {
          l: '◂ Sin alimentar',
          c: '#8793a6',
          v: fm(x.idle)
        }, {
          l: 'Alarmas',
          c: 'oklch(0.66 0.19 25)',
          v: String(x.al)
        }, {
          l: 'Pellets perdidos',
          c: '#b8c1ce',
          v: String(Math.round(x.ppa))
        }, {
          l: 'PPA / min alim.',
          c: '#b8c1ce',
          v: x.feed >= 10 ? (x.ppa / x.feed).toFixed(2) : '–'
        }, {
          l: 'Alimento',
          c: '#b8c1ce',
          v: x.kg.toFixed(0) + ' kg'
        }],
        foot: M[hov.c].fast && x.feed + x.idle === 0 ? 'Ayuno' : 'Clic → Detalle diario de ' + (scope === 'all' ? c.name : c.name)
      };
    }
    const TF = tot.reduce((s, x) => s + x.feed, 0),
      TP = tot.reduce((s, x) => s + x.ppa, 0),
      avg = TF ? TP / TF : 0;
    const cand = J.map((j, r) => ({
        j,
        x: tot[r]
      })).filter(o => TF && o.x.feed / TF >= 0.03),
      crit = cand.length && avg ? cand.reduce((m, o) => o.x.ppa / o.x.feed > m.x.ppa / m.x.feed ? o : m) : null;
    let depIns = {
      lab: '–',
      txt: 'Sin alimentación en el período'
    };
    if (crit) {
      const ci = J.indexOf(crit.j),
        share = cols.map((c, k) => ({
          c,
          v: M[k].acc[crit.j].ppa
        })).sort((a, b) => b.v - a.v)[0];
      depIns = {
        lab: crit.j + '-' + (crit.j + 1) + ' m',
        txt: (crit.x.ppa / crit.x.feed / avg).toFixed(1) + '× PPA por minuto alimentando vs el promedio · ' + crit.x.al + ' alarmas · ' + (crit.x.feed / TF * 100).toFixed(0) + ' % del tiempo alimentando' + (share && share.v > 0 && crit.x.ppa ? '. Mayor aporte: ' + share.c.name + ' (' + (share.v / crit.x.ppa * 100).toFixed(0) + ' %)' : '')
      };
    }
    const seg = (list, cur, field) => list.map(([k, label]) => ({
      label,
      bg: cur === k ? 'rgba(46,179,238,0.16)' : 'transparent',
      color: cur === k ? '#e4e9f0' : '#8793a6',
      fw: cur === k ? 600 : 400,
      onClick: () => this.setState({
        [field]: k,
        dpHov: null
      })
    }));
    const mLab = {
      ppa: 'Pellets perdidos',
      al: 'Alarmas',
      feed: 'Tiempo alimentando',
      ppm: 'PPA por min alimentando'
    }[met];
    const fhT = m => m >= 60 ? (m / 60).toFixed(m >= 600 ? 0 : 1) + ' h' : Math.round(m) + ' min';
    const TI = tot.reduce((s, x) => s + x.idle, 0),
      mode = st.dpMode || 'prof',
      sk = st.dpSort || 'a',
      sd = st.dpDir ?? 1;
    const tRows = J.map((j, r) => ({
      a: j,
      ...tot[r],
      ppm: tot[r].feed >= 10 ? tot[r].ppa / tot[r].feed : -1
    })).filter(x => x.feed + x.idle > 0).sort((p, q) => (p[sk] - q[sk]) * sd);
    const crJ = crit ? crit.j : null;
    const depTable = tRows.map(x => ({
      lab: x.a + '-' + (x.a + 1) + ' m',
      labC: x.a === crJ ? 'oklch(0.72 0.17 25)' : '#e4e9f0',
      bg: x.a === crJ ? 'oklch(0.6 0.19 25 / 0.12)' : 'transparent',
      feed: fhT(x.feed) + ' · ' + (TF ? Math.round(x.feed / TF * 100) : 0) + '%',
      idle: fhT(x.idle) + ' · ' + (TI ? Math.round(x.idle / TI * 100) : 0) + '%',
      al: String(x.al),
      alC: x.al ? 'oklch(0.72 0.17 25)' : '#8793a6',
      ppa: String(Math.round(x.ppa)),
      kg: x.kg.toFixed(1),
      ppm: x.ppm < 0 ? '–' : x.ppm.toFixed(2)
    }));
    const depTHead = [['a', 'Prof.', 'left'], ['feed', 'Alimentando', 'right'], ['idle', 'Sin alim.', 'right'], ['al', 'Alarm.', 'right'], ['ppa', 'PPA', 'right'], ['kg', 'Kg', 'right'], ['ppm', 'PPA/min', 'right']].map(([k, l, al]) => ({
      l: l + (sk === k ? sd > 0 ? ' ▲' : ' ▼' : ''),
      al,
      c: sk === k ? '#e4e9f0' : '#8793a6',
      onClick: () => this.setState(s => ({
        dpSort: k,
        dpDir: s.dpSort === k ? -(s.dpDir ?? 1) : k === 'a' ? 1 : -1
      }))
    }));
    const lane = st.dpLane || 'ppa',
      shMx = Math.max(1e-9, ...tot.map(x => Math.max(TF ? x.feed / TF : 0, TI ? x.idle / TI : 0)));
    const aMx = Math.max(1, ...tot.map(x => x.al)),
      lMx = Math.max(1e-9, ...tot.map(x => lane === 'kg' ? x.kg : x.ppa)),
      ph = st.dpPH;
    const hatch = c => 'repeating-linear-gradient(135deg,' + c + " 0 0.277778vh,transparent 0.277778vh 0.555556vh)";
    const depProf = J.map((j, r) => {
      const x = tot[r],
        has = x.feed + x.idle > 0,
        fs = TF ? x.feed / TF : 0,
        is = TI ? x.idle / TI : 0,
        lv = lane === 'kg' ? x.kg : x.ppa,
        low = has && fs > 0 && fs < 0.03,
        on = ph === r;
      const w = s => s > 0 ? "max(0.104167vw, " + (s / shMx * 100).toFixed(1) + '%)' : '0';
      return {
        lab: j + '-' + (j + 1),
        labC: !has ? '#3a4558' : j === crJ ? 'oklch(0.72 0.17 25)' : '#e4e9f0',
        fw: has ? 600 : 400,
        bg: on ? 'rgba(46,179,238,0.1)' : 'transparent',
        iP: has && is >= 0.005 ? Math.round(is * 100) + '%' : '',
        iW: w(is),
        fP: has ? fs >= 0.005 ? Math.round(fs * 100) + '%' : '-' : '',
        fW: w(fs),
        fBg: low ? hatch('oklch(0.72 0.15 150)') : 'oklch(0.72 0.15 150)',
        aBg: !has ? hatch('#1c2636') : x.al ? 'oklch(0.66 0.2 25 / ' + (0.35 + 0.65 * x.al / aMx).toFixed(2) + ')' : 'rgba(46,179,238,0.05)',
        aBd: has ? "0.092593vh solid rgba(46,179,238,0.1)" : "0.092593vh solid transparent",
        lBg: !has ? hatch('#1c2636') : lv > 0.5 ? 'rgba(184,193,206,' + (0.12 + 0.75 * lv / lMx).toFixed(2) + ')' : low ? hatch('#2a3446') : 'rgba(46,179,238,0.05)',
        enter: () => this.setState({
          dpPH: r
        })
      };
    });
    let depPTip = {
      on: false,
      rows: []
    };
    if (ph != null && ph < J.length && tot[ph].feed + tot[ph].idle > 0) {
      const x = tot[ph];
      depPTip = {
        on: true,
        title: 'Estrato ' + J[ph] + '-' + (J[ph] + 1) + ' m',
        top: ((ph + 0.5) / J.length * 100).toFixed(2) + '%',
        rows: [{
          l: '▸ Alimentando',
          c: 'oklch(0.72 0.15 150)',
          v: fm(x.feed) + ' · ' + (TF ? (x.feed / TF * 100).toFixed(0) : 0) + ' %'
        }, {
          l: '◂ Sin alimentar',
          c: '#8793a6',
          v: fm(x.idle) + ' · ' + (TI ? (x.idle / TI * 100).toFixed(0) : 0) + ' %'
        }, {
          l: 'Alarmas',
          c: 'oklch(0.66 0.19 25)',
          v: String(x.al)
        }, {
          l: 'Perdidos',
          c: '#b8c1ce',
          v: String(Math.round(x.ppa))
        }, {
          l: 'Alimento',
          c: '#b8c1ce',
          v: x.kg.toFixed(1) + ' kg'
        }]
      };
    }
    const depLanes = [['ppa', 'Pellet perdido (PPA)'], ['kg', 'Alimento (kg)']].map(([k, label]) => ({
      label,
      bg: lane === k ? 'rgba(46,179,238,0.16)' : 'transparent',
      color: lane === k ? '#e4e9f0' : '#8793a6',
      onClick: () => this.setState({
        dpLane: k
      })
    }));
    const AL = tot.reduce((s, x) => s + x.al, 0),
      cov = (TF + TI) / Math.max(1, TF + TI + W.nd),
      nf = v => v.toLocaleString('es-CL'),
      fh = m => m >= 600 ? nf(Math.round(m / 60)) + ' h' : fm(m);
    const kv = (l, v, c) => ({
      l,
      v,
      c: c || '#e4e9f0'
    });
    const depWin = {
      cov: Math.round(cov * 100) + ' %',
      covC: cov < 0.5 ? 'oklch(0.8 0.15 75)' : cov < 0.8 ? '#e4e9f0' : 'oklch(0.78 0.14 160)',
      covDeg: (cov * 360).toFixed(0) + 'deg',
      tiempo: [kv('Alimentando', fh(TF)), kv('Sin alimentar', fh(TI)), kv('Sin dato de winche', fh(W.nd), W.nd > 0 ? 'oklch(0.8 0.15 75)' : null), kv('En tránsito', fh(W.tr))],
      rep: nf(W.rep),
      opW: (W.rep ? W.op / W.rep * 100 : 0).toFixed(1) + '%',
      opL: 'Por operador ' + nf(W.op),
      ruL: 'Por rutina ' + nf(W.rep - W.op),
      winche: [kv('Rango', W.dMin.toFixed(1) + ' – ' + W.dMax.toFixed(1) + ' m'), kv('Equipos', String(W.eq.size))],
      alarmas: [kv('Con profundidad', nf(AL)), kv('Sin dato de profundidad', nf(W.alN)), kv('Bajo el umbral (< 0.5 m)', String(W.alLow), W.alLow ? 'oklch(0.8 0.15 75)' : 'oklch(0.8 0.15 75)')]
    };
    return {
      depWin,
      depProf,
      depPTip,
      depLanes,
      depLaneLab: lane === 'kg' ? 'KG' : 'PPA',
      depLaneLeg: lane === 'kg' ? 'Alimento (kg)' : 'Perdidos',
      depLaneMax: '0 – ' + (lane === 'kg' ? lMx.toFixed(1) : Math.round(lMx)) + ' máx',
      depAlMax: '0 – ' + aMx + ' máx',
      depIsProf: mode === 'prof',
      depPLeave: () => this.setState({
        dpPH: null
      }),
      depTable,
      depTHead,
      depIsMap: mode === 'map',
      depIsTab: mode === 'tab',
      depTabN: depTable.length + ' estratos con dato',
      depModes: [['prof', 'Perfil'], ['map', 'Mapa'], ['tab', 'Tabla']].map(([k, label]) => ({
        label,
        bg: mode === k ? 'rgba(46,179,238,0.16)' : 'transparent',
        color: mode === k ? '#e4e9f0' : '#8793a6',
        onClick: () => this.setState({
          dpMode: k,
          dpHov: null
        })
      })),
      depScope: seg([['all', 'Todas las jaulas'], ['cage', D.cages[st.sel]]], scope, 'dpScope'),
      depPer: seg([[7, '7 d'], [14, '14 d'], [30, '30 d'], ['all', 'Todo']], per, 'dpPer'),
      depMet: seg([['ppa', 'PPA'], ['al', 'Alarmas'], ['feed', 'Alimentando'], ['ppm', 'PPA/min']], met, 'dpMet'),
      depRows,
      depHead,
      depTip,
      depIns,
      depCols: "2.708333vw repeat(" + nC + ", minmax(0,1fr)) 6.25vw",
      depLeave: () => this.setState({
        dpHov: null
      }),
      depTitle: mLab + ' por estrato · ' + (scope === 'all' ? 'jaula' : 'día'),
      depSub: (scope === 'all' ? D.rows.length + (this.props.nivelSeleccionado && this.props.nivelSeleccionado !== 'centro' ? ' centros' : ' jaulas') : D.cages[st.sel]) + ' · ' + D.days[start] + ' – ' + D.days[end] + ' (' + days.length + ' días)',
      depLeg: [0.1, 0.3, 0.5, 0.7, 0.9].map(k => ({
        c: 'oklch(' + (0.34 + 0.42 * k).toFixed(3) + ' ' + (0.05 + 0.15 * k).toFixed(3) + ' ' + hue + ')'
      })),
      depMax: '≥ ' + fmtV(mx),
      depMin: '≤ ' + fmtV(lo),
      depTotLab: met === 'ppm' ? 'Promedio' : 'Total'
    };
  }
};
export default logic;
